import { compact } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { ApiOrgProperty, ApiOrgPropertyFacts, ApiUpdateOrgProperty } from '@kaiizen/kai-pro-api';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useOrgMarket, useOrgMarkets, useOrgProperty } from '@kaiizen/kai-pro-api-react-state';
import { usePropertyIdLabel } from '../Lib';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';
import { LabeledInput, LabeledSelect, PropsCard } from '../Widgets';
import { Button } from '../Widgets/Button';

const toStringValue = (value: number | string | boolean | null | undefined): string => value != null ? String(value) : '';

type PropertyMutators = {
  update: (id: string, updates: ApiUpdateOrgProperty) => Promise<ApiOrgProperty>;
};

const safeParseInt = (value: string | number | null | undefined) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

const safeParseFloat = (value: string | number | null | undefined) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

export type PropertyFactUpdateMutator = (fields: Partial<ApiOrgPropertyFacts>) => void;

const COMPARISON_CONTAINER_STYLE = css`
  label: COMPARISON_CONTAINER_STYLE;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const COMPARISON_VALUE_STYLE = css`
  label: COMPARISON_VALUE_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--red);
  text-decoration: line-through;
`;

const LabeledInputWithComparisonValue = (
  { width, value, comparisonValue, updatePropertyFact, updateComparisonPropertyFact, getUpdateFromValue, title, unit, children }: {
    value: string | undefined;
    comparisonValue?: string | null;
    updatePropertyFact: PropertyFactUpdateMutator;
    updateComparisonPropertyFact?: PropertyFactUpdateMutator;
    getUpdateFromValue: (value: string) => Partial<ApiOrgPropertyFacts>;
    title: string;
    unit?: string;
    width: string;
    children?: React.ReactNode;
  },
) => {
  const currentValue = value ?? '';
  const showComparison = comparisonValue != null && comparisonValue !== '' && comparisonValue !== currentValue;

  return (
    <div css={COMPARISON_CONTAINER_STYLE}>
      {showComparison && <span css={COMPARISON_VALUE_STYLE}>{comparisonValue}</span>}
      {showComparison && updateComparisonPropertyFact && (
        <Button
          size='small'
          buttonStyle='green'
          onClick={() => updateComparisonPropertyFact?.(getUpdateFromValue(currentValue))}
        >
          Accept
        </Button>
      )}
      {showComparison && comparisonValue != null && (
        <Button
          size='small'
          buttonStyle='green'
          onClick={() => updatePropertyFact?.(getUpdateFromValue(comparisonValue))}
        >
          Revert
        </Button>
      )}
      {children ?? (
        <LabeledInput
          width={width}
          size='tightMedium'
          value={currentValue}
          onUpdate={value => updatePropertyFact(getUpdateFromValue(value))}
          title={title}
          unit={unit}
        />
      )}
    </div>
  );
};

const formatNumber = (value: number | null | undefined, unit?: string, decimalPlaces?: number): string | undefined => {
  if (value == null) return undefined;
  const formatted = decimalPlaces != null ? value.toFixed(decimalPlaces) : String(value);
  return unit ? `${formatted} ${unit}` : formatted;
};

const formatBoolean = (value: boolean | null | undefined): string | undefined => value === true ? 'Yes' : value === false ? 'No' : undefined;

export const PropertyCard = (
  { extraFields, orgProperty, updatePropertyFact, updateComparisonPropertyFact, propertyUri, noLinkButton, editable, editingFacts, comparisonFacts }: {
    orgProperty: ApiOrgProperty;
    extraFields?: boolean;
    updatePropertyFact?: PropertyFactUpdateMutator;
    updateComparisonPropertyFact?: PropertyFactUpdateMutator;
    propertyUri?: string | undefined;
    noLinkButton?: boolean;
    editable?: boolean;
    editingFacts?: ApiOrgPropertyFacts | undefined;
    comparisonFacts?: ApiOrgPropertyFacts | undefined;
  },
) => {
  const navigate = useNavigate();
  const facts = editingFacts ?? orgProperty.customerMetadata;
  const { orgMarket } = useOrgMarket(extraFields ? orgProperty.orgMarketId : undefined);
  const { markets } = useOrgMarkets();
  const { propertyMutators } = useOrgProperty(orgProperty.id);
  const propertyIdLabel = usePropertyIdLabel();
  const INPUT_WIDTH = comparisonFacts ? LAYOUT2.spacing * 12 + 'px' : LAYOUT2.spacing * 25 + 'px';

  const getFactValue = useCallback((fact: keyof ApiOrgPropertyFacts, decimalPlaces?: number) => {
    if (decimalPlaces != null) {
      return Number(facts?.[fact] ?? comparisonFacts?.[fact] ?? 0).toFixed(decimalPlaces);
    }
    return toStringValue(facts?.[fact] ?? comparisonFacts?.[fact]);
  }, [facts, comparisonFacts]);

  const getComparisonFactValue = useCallback((fact: keyof ApiOrgPropertyFacts, decimalPlaces?: number) => {
    if (!comparisonFacts) return undefined;
    if (decimalPlaces != null) {
      return Number(comparisonFacts?.[fact] ?? 0).toFixed(decimalPlaces);
    }
    return toStringValue(comparisonFacts?.[fact]);
  }, [comparisonFacts]);

  const getFactBooleanValue = useCallback((fact: keyof ApiOrgPropertyFacts): 'yes' | 'no' | '' => {
    const value = facts?.[fact] ?? comparisonFacts?.[fact];
    return value === true ? 'yes' : value === false ? 'no' : '';
  }, [facts, comparisonFacts]);

  const getComparisonFactBooleanValue = useCallback((fact: keyof ApiOrgPropertyFacts): 'yes' | 'no' | '' => {
    return !comparisonFacts ? '' : comparisonFacts?.[fact] === true ? 'yes' : comparisonFacts?.[fact] === false ? 'no' : '';
  }, [comparisonFacts]);

  // Read-only mode
  if (!editable || !updatePropertyFact) {
    return (
      <PropsCard
        title='Property'
        buttonLabel={noLinkButton ? undefined : 'View Property'}
        onButtonClick={noLinkButton ? undefined : () => navigate(propertyUri ?? `/property/${orgProperty?.id}`)}
        items={compact([
          extraFields && facts?.propertyId != null ? { label: propertyIdLabel, value: String(facts.propertyId) } : undefined,
          extraFields && orgMarket?.name != null ? { label: 'Market', value: orgMarket.name } : undefined,
          facts?.bedroomCount != null ? { label: 'Beds', value: String(facts.bedroomCount) } : undefined,
          facts?.fullBathCount != null ? { label: 'Full Baths', value: String(facts.fullBathCount) } : undefined,
          facts?.halfBathCount != null ? { label: 'Half Baths', value: String(facts.halfBathCount) } : undefined,
          facts?.squareFeet != null ? { label: 'Square Feet', value: formatNumber(facts.squareFeet, 'sqft', 0) } : undefined,
          facts?.acres != null ? { label: 'Acres', value: formatNumber(facts.acres, 'acres', 3) } : undefined,
          facts?.hasGarage != null ? { label: 'Garage', value: formatBoolean(facts.hasGarage) } : undefined,
        ])}
      />
    );
  }

  // Editable mode
  return (
    <PropsCard
      alignLabels='center'
      title='Property'
      buttonLabel={noLinkButton ? undefined : 'View Property'}
      onButtonClick={noLinkButton ? undefined : () => navigate(propertyUri ?? `/property/${orgProperty?.id}`)}
      items={[
        extraFields
          ? {
            label: propertyIdLabel,
            value: (
              <LabeledInput
                width={INPUT_WIDTH}
                size='small'
                value={String(facts?.propertyId ?? '')}
                onUpdate={value => updatePropertyFact({ propertyId: value })}
              />
            ),
          }
          : undefined,
        extraFields
          ? {
            label: 'Market',
            value: (
              <LabeledSelect
                width={INPUT_WIDTH}
                size='small'
                value={orgMarket?.id ?? ''}
                onUpdate={value => propertyMutators.update(orgProperty.id, { orgMarketId: value })}
                items={markets?.map(m => ({ label: m.name, value: m.id })) ?? []}
              />
            ),
          }
          : undefined,

        {
          label: 'Beds',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactValue('bedroomCount')}
              comparisonValue={getComparisonFactValue('bedroomCount')}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => ({ bedroomCount: safeParseInt(value) })}
              updatePropertyFact={updatePropertyFact}
              title='Number of bedrooms in the property'
            />
          ),
        },
        {
          label: 'Full Baths',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactValue('fullBathCount')}
              comparisonValue={getComparisonFactValue('fullBathCount', 0)}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => {
                const fullBathCount = safeParseInt(value) ?? 0;
                return { fullBathCount, bathroomCount: fullBathCount + (facts?.halfBathCount ?? 0) };
              }}
              updatePropertyFact={updatePropertyFact}
              title='Number of full bathrooms (with tub or shower)'
            />
          ),
        },
        {
          label: 'Half Baths',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactValue('halfBathCount')}
              comparisonValue={getComparisonFactValue('halfBathCount')}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => {
                const halfBathCount = safeParseInt(value) ?? 0;
                return { halfBathCount, bathroomCount: (facts?.fullBathCount ?? 0) + halfBathCount };
              }}
              updatePropertyFact={updatePropertyFact}
              title='Number of half bathrooms (toilet and sink only)'
            />
          ),
        },
        {
          label: 'Square Feet',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactValue('squareFeet', 0)}
              comparisonValue={getComparisonFactValue('squareFeet', 0)}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => ({ squareFeet: safeParseFloat(value) })}
              updatePropertyFact={updatePropertyFact}
              unit='sqft'
              title='Total square footage of the property'
            />
          ),
        },
        {
          label: 'Acres',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactValue('acres', 3)}
              comparisonValue={getComparisonFactValue('acres', 3)}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => ({ acres: safeParseFloat(value) })}
              updatePropertyFact={updatePropertyFact}
              unit='acres'
              title='Lot size in acres'
            />
          ),
        },
        {
          label: 'Garage',
          value: (
            <LabeledInputWithComparisonValue
              width={INPUT_WIDTH}
              value={getFactBooleanValue('hasGarage')}
              comparisonValue={getComparisonFactBooleanValue('hasGarage')}
              updatePropertyFact={updatePropertyFact}
              updateComparisonPropertyFact={updateComparisonPropertyFact}
              getUpdateFromValue={value => ({ hasGarage: value === 'yes' ? true : value === 'no' ? false : null })}
              title='Does the property have a garage?'
            >
              <LabeledSelect
                width={INPUT_WIDTH}
                size='tightMedium'
                value={getFactBooleanValue('hasGarage')}
                items={[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }]}
                onUpdate={value => updatePropertyFact({ hasGarage: value === 'yes' ? true : value === 'no' ? false : null })}
                title='Does the property have a garage?'
                placeholder='Select...'
              />
            </LabeledInputWithComparisonValue>
          ),
        },
      ]}
    />
  );
};
